import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search, FileText, Menu, LogOut, Brain } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export default function BottomNav({ onOpenMobileSidebar, onOpenRightSidebar }) {
  const { isRTL, language } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  // Let's create a NavItem component
  const NavItem = ({ item }) => {
    const active = item.path && isActive(item.path);
    return (
      <button
        onClick={() => item.action ? item.action() : navigate(item.path)}
        className={`flex flex-col items-center justify-center w-[60px] h-[52px] rounded-xl transition-all ${
          active ? 'bg-[#2F80ED]/10' : 'hover:bg-black/5'
        }`}
      >
        <item.icon className={`w-[22px] h-[22px] mb-1 ${active ? 'text-[#2F80ED]' : item.color || 'text-[#64748B]'}`} strokeWidth={active ? 2.5 : 2} />
        <span className={`text-[10px] font-bold ${active ? 'text-[#2F80ED]' : item.color || 'text-[#64748B]'}`}>
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[70] md:hidden pointer-events-none pb-4 px-4">
      <div 
        className="bg-[#F8FAF8]/90 backdrop-blur-xl border border-white shadow-[0_-4px_20px_rgba(0,0,0,0.05)] rounded-[24px] relative flex items-center justify-between px-2 py-1.5 pointer-events-auto" 
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Right side items (Home, CV Builder) */}
        <div className="flex items-center gap-1 flex-1 justify-around">
          <NavItem item={{ id: 'home', icon: Home, label: language === 'ar' ? 'الرئيسية' : 'Home', path: '/dashboard' }} />
          <NavItem item={{ id: 'cv', icon: FileText, label: language === 'ar' ? 'السيرة' : 'CV', path: '/cv-builder' }} />
        </div>

        {/* Center Brain Button */}
        <div className="relative w-[70px] flex justify-center">
          <div className="absolute -top-7">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/chatbot')}
              className="w-[56px] h-[56px] rounded-full bg-[#2F80ED] border-[3px] border-[#F8FAF8] flex items-center justify-center shadow-lg"
            >
              <Brain className="w-7 h-7 text-white" />
            </motion.button>
          </div>
        </div>

        {/* Left side items (Jobs, More) */}
        <div className="flex items-center gap-1 flex-1 justify-around">
          <NavItem item={{ id: 'jobs', icon: Search, label: language === 'ar' ? 'الوظائف' : 'Jobs', path: '/job-search' }} />
          <NavItem item={{ id: 'more', icon: Menu, label: language === 'ar' ? 'المزيد' : 'More', action: onOpenMobileSidebar }} />
        </div>
      </div>
    </div>
  );
}
